import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
}

interface CollapsibleFAQProps {
  faqs: FAQItem[];
  title?: string;
  className?: string;
}

export default function CollapsibleFAQ({ 
  faqs, 
  title = "Frequently Asked Questions",
  className = ""
}: CollapsibleFAQProps) {
  const [expandedItems, setExpandedItems] = useState<Record<number, boolean>>({});

  const toggleItem = (index: number) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {title && <h2 className="text-2xl font-bold text-gray-900 mb-6">{title}</h2>}
      <div className="space-y-4" itemScope itemType="https://schema.org/FAQPage">
        {faqs.map((faq, index) => (
          <div 
            key={index} 
            className="border border-gray-200 rounded-lg overflow-hidden"
            itemScope 
            itemProp="mainEntity" 
            itemType="https://schema.org/Question"
          >
            <button 
              className="w-full bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center text-left"
              onClick={() => toggleItem(index)}
              aria-expanded={expandedItems[index] || false}
              aria-controls={`faq-content-${index}`}
            >
              <h3 className="font-medium text-gray-900" itemProp="name">{faq.question}</h3>
              {expandedItems[index] ? (
                <ChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </button>
            <div 
              id={`faq-content-${index}`}
              className={`overflow-hidden transition-all duration-300 ${
                expandedItems[index] ? 'max-h-96 p-4' : 'max-h-0'
              }`}
              itemScope 
              itemProp="acceptedAnswer" 
              itemType="https://schema.org/Answer"
            >
              <div className="text-gray-600" itemProp="text" dangerouslySetInnerHTML={{ __html: faq.answer }}></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}